/**
 * Application constants
 */

export const CURRENCIES = [
  { value: 'USD', label: 'US Dollar ($)' },
  { value: 'EUR', label: 'Euro (€)' },
  { value: 'GBP', label: 'British Pound (£)' },
  { value: 'JPY', label: 'Japanese Yen (¥)' },
  { value: 'CHF', label: 'Swiss Franc (CHF)' },
  { value: 'CAD', label: 'Canadian Dollar (C$)' },
  { value: 'AUD', label: 'Australian Dollar (A$)' },
  { value: 'CNY', label: 'Chinese Yuan (¥)' },
  { value: 'INR', label: 'Indian Rupee (₹)' },
  { value: 'SGD', label: 'Singapore Dollar (S$)' },
  { value: 'NZD', label: 'New Zealand Dollar (NZ$)' },
  { value: 'HKD', label: 'Hong Kong Dollar (HK$)' },
  { value: 'SEK', label: 'Swedish Krona (kr)' },
  { value: 'NOK', label: 'Norwegian Krone (kr)' },
  { value: 'DKK', label: 'Danish Krone (kr)' },
  { value: 'KRW', label: 'South Korean Won (₩)' },
  { value: 'BRL', label: 'Brazilian Real (R$)' },
  { value: 'RUB', label: 'Russian Ruble (₽)' },
  { value: 'ZAR', label: 'South African Rand (R)' },
  { value: 'MXN', label: 'Mexican Peso (Mex$)' },
  { value: 'VND', label: 'Vietnamese Dong (₫)' },
] as const;

export type CurrencyCode = (typeof CURRENCIES)[number]['value'];

export const DEFAULT_CURRENCY: CurrencyCode = 'USD';

export const FREQUENCIES = [
  { value: 'one_time', label: 'One-time' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' },
  { value: 'first_year', label: 'First Year' },
] as const;

export type FrequencyCode = (typeof FREQUENCIES)[number]['value'];

export const DEFAULT_FREQUENCY: FrequencyCode = 'one_time';

export const BENEFIT_TYPES = [
  { value: 'medical', label: 'Medical' },
  { value: 'dental', label: 'Dental' },
  { value: 'vision', label: 'Vision' },
  { value: 'remote_work', label: 'Remote Work' },
  { value: '401k', label: '401(k)' },
  { value: 'education_stipend', label: 'Education Stipend' },
  { value: 'wellness_program', label: 'Wellness Program' },
  { value: 'flexible_hours', label: 'Flexible Hours' },
  { value: 'home_office_stipend', label: 'Home Office Stipend' },
  { value: 'pto', label: 'PTO' },
  { value: 'other', label: 'Other' },
] as const;

export type BenefitType = (typeof BENEFIT_TYPES)[number]['value'];

export type BenefitTemplateDefaults = {
  name: string;
  description: string;
  monetaryValue?: number;
  frequency?: FrequencyCode;
  currency?: CurrencyCode;
};

export const BENEFIT_TEMPLATE_DEFAULTS: Record<BenefitType, BenefitTemplateDefaults> = {
  medical: {
    name: 'Health Insurance',
    description:
      'Comprehensive medical coverage including preventive care, prescriptions, and emergency services. Company covers 100% of premiums for employees and 80% for family coverage.',
  },
  dental: {
    name: 'Dental Insurance',
    description:
      'Full dental coverage including routine cleanings, fillings, and major dental work. No waiting periods for preventive care.',
  },
  vision: {
    name: 'Vision Insurance',
    description:
      'Complete vision care including annual eye exams, prescription glasses, and contact lenses. $300 annual allowance for frames.',
  },
  remote_work: {
    name: 'Remote Work Flexibility',
    description:
      'Work from anywhere with full remote work support. Flexible schedule with core collaboration hours from 10 AM - 3 PM local time.',
  },
  '401k': {
    name: '401(k) Retirement Plan',
    description:
      '6% company match on 401(k) contributions with immediate vesting. Access to low-cost investment options and financial planning resources.',
  },
  education_stipend: {
    name: 'Learning & Development Budget',
    description:
      '$2,000 annual budget for courses, conferences, certifications, and professional development. Additional time off for learning activities.',
    monetaryValue: 2000,
    frequency: 'annually',
    currency: 'USD',
  },
  wellness_program: {
    name: 'Wellness Program',
    description:
      'Comprehensive wellness benefits including gym membership reimbursement, mental health support, and quarterly wellness days off.',
    monetaryValue: 200,
    frequency: 'monthly',
    currency: 'USD',
  },
  flexible_hours: {
    name: 'Flexible Working Hours',
    description:
      'Flexible start and end times to support work-life balance. Core collaboration hours with freedom to adjust your schedule around personal needs.',
  },
  home_office_stipend: {
    name: 'Home Office Stipend',
    description:
      '$1,500 one-time setup allowance plus $500 annual budget for home office equipment, furniture, and improvements.',
    monetaryValue: 2000,
    frequency: 'first_year',
    currency: 'USD',
  },
  pto: {
    name: 'Paid Time Off',
    description:
      '25 days PTO plus 12 company holidays and your birthday off. Unlimited sick days and flexible mental health days as needed.',
  },
  other: {
    name: '',
    description: '',
  },
};

/**
 * Status filter options for UI
 * Note: Status types and interfaces are in @lib/api/types.ts
 */
export const STATUS_FILTER_OPTIONS = [
  { value: 'all-statuses', label: 'All' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'pending', label: 'Pending' },
  { value: 'declined', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
  { value: 'reneged', label: 'Reneged' },
  { value: 'rescinded', label: 'Rescinded' },
] as const;

/**
 * Comp-chart growth-projection slider bounds. The Python serializer
 * (core/api/serializers/company.py, comp_chart_max_multiplier) and model
 * validators are authoritative; keep these in step with them.
 */
export const COMP_CHART_MULTIPLIER_MIN = 2;
export const COMP_CHART_MULTIPLIER_MAX = 100;
export const COMP_CHART_MULTIPLIER_DEFAULT = 30;
export const COMP_CHART_DISCLAIMER_MAX_LENGTH = 500;

/** Standard copy under the comp chart when a company sets no custom disclaimer. */
export const COMP_CHART_DEFAULT_DISCLAIMER =
  'This simulator provides estimates based on the given inputs. Actual equity outcomes depend ' +
  'on company performance and market conditions.';
